import { File, HardDriveDownload } from "lucide-react";
import { FileMessageFile } from "../FileMessage";
import { formatFileSize } from "@/utils/file";
import OptionMenuWithError from "../OptionMenuWithError";
import LocationLink from "../LocationLink";
import "../messageStyles.css";
import { USERID_ADDRESS_TYPE } from "@/constants/message";
import { resendMessage } from "@/features/chat/chatSlice";
import {
  selectCurrConversation,
  selectConversations,
} from "@/features/control/controlSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { Conversation } from "@/types/conversation";
import { useCallback } from "react";

interface SentFileMessageProps {
  id: number;
  created: string;
  time: string;
  file: FileMessageFile;
  dkb: number;
  error?: boolean;
}

const SentFileMessage = ({
  id,
  created,
  time,
  file,
  dkb,
  error,
}: SentFileMessageProps) => {
  const dispatch = useAppDispatch();
  const currConversationIndex = useAppSelector(selectCurrConversation);
  const conversations: Conversation[] = useAppSelector(selectConversations);

  const handleResend = useCallback(async () => {
    const addressId = conversations[currConversationIndex!].addressId;
    const address = conversations[currConversationIndex!].address;
    const addressType = USERID_ADDRESS_TYPE;

    await dispatch(
      resendMessage({
        address,
        addressId,
        addressType,
        message: JSON.stringify(file),
      })
    );
  }, [dispatch, conversations, currConversationIndex]);

  return (
    <div key={id} className="message-container">
      <div className="message-time-container items-end">
        <span className="message-time">{created ? time : "now"}</span>
        <OptionMenuWithError error={error} handleResend={handleResend}>
          <div className="flex items-center gap-2 p-4 sent-color rounded-lg min-w-0">
            <div className="file-message-file-container">
              <File size={32} className="file-item" />
              <div className="file-info-container">
                <p className="file-name">{file.fname}</p>
                <p className="file-size">{formatFileSize(dkb)}</p>
              </div>
              <div className="file-preview-container">
                <a
                  href={`data:image/webp;base64,${file.media}`}
                  download={file.dname}
                  title="Download File"
                  className="file-item"
                >
                  <HardDriveDownload size={20} />
                </a>
              </div>
            </div>
          </div>
        </OptionMenuWithError>
        {/* File caption */}
        {file.msg.length > 0 && (
          <OptionMenuWithError error={error}>
            <div className="message-text-container sent-color max-w-fit">
              <p className="attachment-message">{file.msg}</p>
            </div>
          </OptionMenuWithError>
        )}
        <LocationLink lat={file.lat} lng={file.lng} />
      </div>
    </div>
  );
};

export default SentFileMessage;
